import { ApiClientProvider } from '@hostesa/api-client';
import { AuthProvider } from '@hostesa/auth-client';
import { I18nProvider } from '@hostesa/i18n';
import { HostesaErrorBoundary, ObservabilityProvider } from '@hostesa/observability';
import { StateProvider } from '@hostesa/state';
import { ErrorState } from '@hostesa/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@hostesa/ui/styles.css';

import { APP_I18N_RESOURCES } from './i18n/resources';
import { setAuthSnapshot } from './lib/auth-bridge';
import { AuthBridge } from './lib/auth-bridge-react';
import { bootstrapApp } from './lib/bootstrap';
import { consumeImpersonationHandoff } from './lib/impersonation-handoff';
import { RuntimeProvider } from './lib/runtime-context';
import { createAppRouter } from './router';

/**
 * Bootstrap order (provider composition):
 *   ErrorBoundary > ObservabilityProvider > StateProvider (Redux) >
 *   QueryClientProvider > I18nProvider > AuthProvider > AuthBridge >
 *   RuntimeProvider > RouterProvider
 *
 * RealtimeProvider is intentionally NOT at the top: it mounts inside the
 * hotel-scoped subtree (see <HotelGuard>) once the active hotel + token are
 * resolved, so socket rooms are always bound to the correct hotel.
 *
 * The 401 handler clears the session and routes to /login?next=<current>; the
 * router invalidation in <AuthBridge> then re-runs guards.
 */
function start() {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element #root not found');

  // Build the router first so the 401 handler can navigate through it.
  let router: ReturnType<typeof createAppRouter> | null = null;

  const runtime = bootstrapApp({
    onUnauthorized: () => {
      const next = `${window.location.pathname}${window.location.search}`;
      void router?.navigate({
        to: '/login',
        search: { next },
      });
    },
  });

  // If the operator handed us an impersonation token via the URL fragment
  // (#imp=<token>), consume it into the session store BEFORE the first /auth/me
  // request fires, so that request carries the impersonation token. The
  // fragment is stripped from the address bar immediately.
  //
  // We ALSO seed the synchronous auth snapshot here, from the impersonation
  // token if present, otherwise from any token persisted in sessionStorage
  // (a normal page refresh). The router's `sessionGate` reads
  // `getAuthSnapshot().token`, but `AuthBridge` only publishes the React auth
  // state into that snapshot after the first paint — so on a cold load the
  // guard would otherwise see `token: null` and bounce to /login before
  // `AuthProvider` has a chance to restore the session from the store. Seeding
  // `loading: true` lets the route loading-gate wait for `/auth/me` (which
  // re-validates the token and populates memberships) before it decides.
  const impToken = consumeImpersonationHandoff(runtime.sessionStore);
  const bootToken = impToken ?? runtime.sessionStore.getToken();
  if (bootToken !== null) {
    setAuthSnapshot({ token: bootToken, user: null, memberships: [], loading: true });
  }

  router = createAppRouter({ runtime });
  const appRouter = router;

  // Clear the query cache on logout (AuthProvider.onLogout). Realtime is
  // disconnected automatically when <RealtimeProvider> unmounts on navigation
  // away from the hotel subtree.
  const handleLogout = () => {
    runtime.queryClient.clear();
    void appRouter.navigate({ to: '/login' });
  };

  createRoot(rootElement).render(
    <StrictMode>
      <HostesaErrorBoundary fallback={<ErrorState data-testid="app-error-boundary" />}>
        <ObservabilityProvider
          app="platform"
          environment={runtime.config.environment}
          release={`platform@${runtime.config.releaseId}`}
        >
          <StateProvider>
            <QueryClientProvider client={runtime.queryClient}>
              <ApiClientProvider client={runtime.apiClient}>
                <I18nProvider
                  locale={runtime.config.defaultLocale}
                  i18nOptions={{ resources: APP_I18N_RESOURCES }}
                >
                  <AuthProvider
                    apiClient={runtime.apiClient}
                    sessionStore={runtime.sessionStore}
                    onLogout={handleLogout}
                  >
                    <RuntimeProvider runtime={runtime}>
                      <AuthBridge router={appRouter}>
                        <RouterProvider router={appRouter} />
                      </AuthBridge>
                    </RuntimeProvider>
                  </AuthProvider>
                </I18nProvider>
              </ApiClientProvider>
            </QueryClientProvider>
          </StateProvider>
        </ObservabilityProvider>
      </HostesaErrorBoundary>
    </StrictMode>,
  );
}

start();
